export function safeStr(v) {
  if (v === null || v === undefined) return '';
  return String(v);
}

export function getInputValue(arg) {
  if (arg === null || arg === undefined) return '';
  if (typeof arg === 'string' || typeof arg === 'number') return String(arg);
  if (arg?.target && 'value' in arg.target) return safeStr(arg.target.value);
  if (arg && typeof arg === 'object' && 'value' in arg)
    return safeStr(arg.value);
  return safeStr(arg);
}

export function isSelectableSlug(slug) {
  return slug === 'unique' || slug === 'multiple' || slug === 'logic';
}

export function isDescriptiveSlug(slug) {
  return slug === 'descriptive' || slug === 'text' || slug === 'description';
}

export function isMediaSlug(slug) {
  return slug === 'image' || slug === 'video';
}

export function isVideoVisualizationSlug(slug) {
  return slug === 'video_visualization';
}

export function getQuestionTypeSlug(q) {
  return String(q?.type?.slug || '').toLowerCase();
}

export function getQuestionVideoUrl(q) {
  const media = q?.originalQuestion?.media || q?.media || {};

  return media?.full_path || media?.fullPath || media?.url || '';
}

export function isWatchOnlyVideoQuestion(q) {
  return isVideoVisualizationSlug(getQuestionTypeSlug(q));
}

export function isTrueFalseQuestion(q) {
  return getQuestionTypeSlug(q) === 'logic';
}

export function isTrainingPublicQuestionRequired(q) {
  if (isWatchOnlyVideoQuestion(q)) return false;
  if (isTrueFalseQuestion(q)) return !!q?.required;

  return true;
}

export function hasBackAttachments(q) {
  return Array.isArray(q?.attachments) && q.attachments.length > 0;
}

export function hasLocalFiles(q) {
  const files = q?.attachment?.files;
  return !!files && files.length > 0;
}

export function isAnswered(q) {
  const slug = getQuestionTypeSlug(q);

  if (isVideoVisualizationSlug(slug)) return true;

  if (isSelectableSlug(slug))
    return (q.responses || []).some(r => !!r.selected);

  if (isDescriptiveSlug(slug)) {
    const desc = safeStr(q.questionDescriptiveResponse).trim();
    return desc.length > 0;
  }

  if (isMediaSlug(slug))
    return !!q.sentAttachment || hasBackAttachments(q) || hasLocalFiles(q);

  const desc = safeStr(q.questionDescriptiveResponse).trim();
  return (q.responses || []).some(r => !!r.selected) || desc.length > 0;
}

export function isAttachmentRequiredByBackend(q) {
  const slug = getQuestionTypeSlug(q);
  const required = isTrainingPublicQuestionRequired(q);
  const answered = isAnswered(q);

  if (isVideoVisualizationSlug(slug)) return false;
  if (isMediaSlug(slug)) return required || answered;
  if (q?.needAttachment) return required || answered;

  return false;
}

export function shouldRenderUpload(q) {
  const slug = getQuestionTypeSlug(q);
  if (isVideoVisualizationSlug(slug)) return false;
  return isMediaSlug(slug) || !!q?.needAttachment;
}

export function isParentQuestion(q) {
  return !!q && (!q.fatherQuestionId || q.hasSubQuestion);
}

export function isSubQuestion(q) {
  return !!q?.fatherQuestionId;
}

export function shouldShowSubQuestion(parentQuestion = {}, subQuestion = {}) {
  if (
    !parentQuestion?.id ||
    parentQuestion.id !== subQuestion?.fatherQuestionId
  )
    return false;

  const questionTrigger =
    Array.isArray(parentQuestion?.questionTrigger) &&
    parentQuestion.questionTrigger.length
      ? parentQuestion.questionTrigger
      : (parentQuestion.responses || []).filter(
          response => response?.triggerSubQuestion
        );

  if (!questionTrigger.length) return false;

  const isSelectedTrigger = (parentQuestion.responses || []).find(response => {
    return questionTrigger.find(
      trigger => response.id === trigger.id && response.selected
    );
  });

  return !!isSelectedTrigger;
}

export function groupQuestionsWithSubQuestions(list = []) {
  const parents = list.filter(q => isParentQuestion(q) || !isSubQuestion(q));
  const subsByParent = new Map();

  list
    .filter(q => isSubQuestion(q))
    .forEach(sub => {
      const arr = subsByParent.get(sub.fatherQuestionId) || [];
      arr.push(sub);
      subsByParent.set(sub.fatherQuestionId, arr);
    });

  return parents.map(p => ({
    parent: p,
    subQuestions: subsByParent.get(p.id) || [],
  }));
}

export function processParentQuestion(question, allQuestions) {
  const subQuestion = (allQuestions || []).find(
    q => q.fatherQuestionId === question.id
  );

  if (!subQuestion) return question;

  const triggerValueIds = new Set(
    (question.questionSubResponses || [])
      .map(item => item?.questionValueId)
      .filter(Boolean)
  );

  const processedResponses = (question.responses || []).map(response => {
    const links = Array.isArray(response?.subValueResponse)
      ? response.subValueResponse
      : [];
    const triggersSubQuestion = links.some(link => {
      return (
        link?.questionResponseId === subQuestion.fatherQuestionId ||
        link?.questionResponseId === subQuestion.id
      );
    });

    return {
      ...response,
      triggerSubQuestion:
        response?.triggerSubQuestion ||
        triggersSubQuestion ||
        (response?.id ? triggerValueIds.has(response.id) : false),
    };
  });

  return {
    ...question,
    responses: processedResponses,
    questionTrigger: processedResponses.filter(r => r.triggerSubQuestion),
  };
}

function normalizeOptionText(value) {
  return safeStr(value).trim().toLowerCase();
}

function isTruthyFlag(value) {
  if (value === true) return true;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'true' || normalized === '1';
  }
  return false;
}

function getOptionIdentities(option) {
  return [
    option?.id,
    option?.questionValueId ?? option?.question_value_id,
    option?.valueId ?? option?.value_id,
  ]
    .filter(value => value !== null && value !== undefined && value !== '')
    .map(String);
}

function optionsMatchByIdentity(left, right) {
  if (!left || !right) return false;

  const leftIds = getOptionIdentities(left);
  const rightIds = new Set(getOptionIdentities(right));

  if (leftIds.some(id => rightIds.has(id))) {
    return true;
  }

  return (
    normalizeOptionText(left?.description) ===
    normalizeOptionText(right?.description)
  );
}

function getQuestionOptionsForScoring(question) {
  const responseOptions = Array.isArray(question?.responses)
    ? question.responses
    : [];

  const originalOptions =
    (Array.isArray(question?.originalQuestion?.values) &&
      question.originalQuestion.values) ||
    (Array.isArray(question?.values) && question.values) ||
    [];

  const baseOptions = originalOptions.length
    ? originalOptions
    : responseOptions;

  return baseOptions.map(option => {
    const matchedResponse = responseOptions.find(response =>
      optionsMatchByIdentity(response, option)
    );

    return {
      ...option,
      selected: isTruthyFlag(matchedResponse?.selected),
      isCorrect:
        isTruthyFlag(option?.isCorrect) ||
        isTruthyFlag(matchedResponse?.isCorrect),
    };
  });
}

function isQuestionCorrectlyAnswered(options = []) {
  const correctOptions = options.filter(option =>
    isTruthyFlag(option?.isCorrect)
  );
  const selectedOptions = options.filter(option => !!option?.selected);

  if (!correctOptions.length || !selectedOptions.length) return false;
  if (correctOptions.length !== selectedOptions.length) return false;

  return selectedOptions.every(selectedOption => {
    return correctOptions.some(correctOption =>
      optionsMatchByIdentity(selectedOption, correctOption)
    );
  });
}

export function resetQuestionForRetry(question) {
  return {
    ...question,
    questionDescriptiveResponse: '',
    descriptiveResponse: '',
    sentAttachment: false,
    attachment: {
      ...(question?.attachment || {}),
      files: null,
    },
    attachments: [],
    responses: Array.isArray(question?.responses)
      ? question.responses.map(response => ({
          ...response,
          selected: false,
        }))
      : [],
  };
}

export function getTrainingScorePercent(list = []) {
  const groupedQuestions = groupQuestionsWithSubQuestions(list || []);

  const visibleQuestions = groupedQuestions.flatMap(
    ({ parent, subQuestions }) => {
      const visibleSubQuestions = (subQuestions || []).filter(subQuestion =>
        shouldShowSubQuestion(parent, subQuestion)
      );

      return [parent, ...visibleSubQuestions];
    }
  );

  const scorableQuestions = visibleQuestions.filter(q => {
    const slug = getQuestionTypeSlug(q);
    const options = getQuestionOptionsForScoring(q);

    return (
      isSelectableSlug(slug) &&
      options.some(option => isTruthyFlag(option?.isCorrect))
    );
  });

  if (!scorableQuestions.length) return null;

  const correctQuestions = scorableQuestions.filter(q => {
    const options = getQuestionOptionsForScoring(q);

    return isQuestionCorrectlyAnswered(options);
  });

  return Math.round((correctQuestions.length / scorableQuestions.length) * 100);
}

export function getCertificateUrlFromResponse(payload = {}) {
  const data = payload?.data || payload;
  const certificate = data?.certificate || {};

  return (
    data?.certificateUrl ||
    certificate?.certificateUrl ||
    certificate?.mediaUrl ||
    ''
  );
}

export function normalizeShowPayload(data) {
  const payload = data?.data || data;
  if (!payload) return null;

  const raw =
    payload?.questionsResponses ||
    payload?.questions ||
    payload?.data?.questionsResponses ||
    payload?.data?.questions ||
    [];

  const flattened =
    Array.isArray(raw) && raw.length > 0 && Array.isArray(raw[0]?.questions)
      ? raw.flatMap(g =>
          (g.questions || []).map(q => ({
            ...q,
            groupedQuestion: g.group ?? q.groupedQuestion ?? 1,
          }))
        )
      : raw;

  const list = Array.isArray(flattened)
    ? [...flattened].map(q => ({
        ...q,
        attachment: q.attachment || {},
        questionDescriptiveResponse:
          q.questionDescriptiveResponse ?? q.descriptiveResponse ?? '',
        responses: Array.isArray(q.responses) ? q.responses : [],
        attachments: Array.isArray(q.attachments) ? q.attachments : [],
      }))
    : [];

  return {
    ...payload,
    singleChecklistConfig: {
      ...(payload?.singleChecklistConfig || {}),
      anonymous: false,
      isAnonymous: false,
    },
    isAnonymous: false,
    questionsResponses: list,
  };
}

export function getUniqueLinkLockInfo(payload) {
  const link = payload?.link || {};
  const isUnique = !!link?.responseUnique;

  if (!isUnique) return { blocked: false, reason: '' };

  const answeredAt = payload?.answeredAt;
  const responsesCount = Number(payload?.responsesCount) || 0;

  const anyAnsweredInQuestions =
    Array.isArray(payload?.questionsResponses) &&
    payload.questionsResponses.some(q => !!q?.answeredAt);

  const consumed =
    responsesCount >= 1 || !!answeredAt || anyAnsweredInQuestions;

  if (consumed) {
    return { blocked: true, reason: 'Esse link já foi respondido.' };
  }

  return { blocked: false, reason: '' };
}

export function shouldUseOriginalQuestionIdsForPayload(payload) {
  const statusSlug = safeStr(
    payload?.checklistStatus?.slug || payload?.status?.slug
  ).toLowerCase();
  const answeredAt = payload?.answeredAt || payload?.answered_at;
  const responsesCount = Number(
    payload?.responsesCount ??
      payload?.link?.responsesCount ??
      payload?.responseCount ??
      0
  );
  const lastGroup = Number(payload?.lastGroup || 0);

  return (
    responsesCount > 0 ||
    !!answeredAt ||
    statusSlug === 'answered' ||
    lastGroup > 1
  );
}

export function getPayloadQuestionId(question, useOriginalQuestionId) {
  if (!useOriginalQuestionId) return question?.id;

  return (
    question?.originalQuestion?.id ??
    question?.questionId ??
    question?.question_id ??
    question?.id
  );
}
